import { AsideComponent } from './shared/components/aside/aside.component';
import { HeaderComponent } from './shared/components/header/header.component';
import { AsideService } from '@my-backoffice/app/pages/main/shared/components/aside/shared/services/aside.service';
import { FreshChatService } from '@my-backoffice/app/pages/main/shared/services/fresh-chat.service';
import { IUser } from '@my-backoffice/app/pages/user/shared/models/interfaces/user.interface';
import { UserService } from '@my-backoffice/app/pages/user/shared/service/user.service';
import { AUTHENTICATION_SERVICE } from '@my-backoffice/app/shared/auth/authentication.token';
import { IMyWindow } from '@my-backoffice/app/shared/interfaces/my-window.interface';
import { EChannelKeys } from '@my-backoffice/app/shared/models/enums/channel-keys.enum';
import { EStorageKeys } from '@my-backoffice/app/shared/models/enums/storage-keys.enum';
import { StorageService } from '@my-backoffice/app/shared/services/storage-services/storage.service';
import { AppcuesService } from '@my-backoffice/app/shared/services/third-party-services/appcues/appcues.service';
import { AppUserSettingService } from '@my-backoffice/app/shared/services/user-services/app-user-setting/app-user-setting.service';
import { WINDOW } from '@my-backoffice/app/shared/tokens/window.token';
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, Inject, OnInit, ViewEncapsulation } from '@angular/core';
import { Event, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { filter, Observable, tap } from 'rxjs';

@UntilDestroy()
@Component({
  selector: 'app-main',
  standalone: true,
  templateUrl: './main.component.html',
  styleUrls: ['./main.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [HeaderComponent, AsideComponent, RouterOutlet],
})
export class MainComponent implements OnInit, AfterViewInit {
  public readonly isAsideEnabled = false;
  public isAsideCollapsed: boolean = false;
  public isAppcuesBannerVisible = false;

  private readonly _authenticationService = inject(AUTHENTICATION_SERVICE);

  public constructor(
    private readonly _appcuesService: AppcuesService,
    private readonly _router: Router,
    private readonly _changeDetectorRef: ChangeDetectorRef,
    private readonly _asideService: AsideService,
    private readonly _freshChatService: FreshChatService,
    private readonly _storageService: StorageService,
    private readonly _userService: UserService,
    private readonly _appUserSettingService: AppUserSettingService,
    @Inject(WINDOW) private readonly _window: IMyWindow
  ) {}

  public ngOnInit(): void {
    this._watch();
  }

  public ngAfterViewInit(): void {
    const userData: IUser = this._authenticationService.getCurrentUser();

    this._initChat(userData);

    this._authenticationService.currentUserSubject$.pipe(untilDestroyed(this)).subscribe((data: IUser): void => {
      this._initChat(data);
    });
  }

  private _initChat(userData: IUser): void {
    if (
      this._authenticationService.isAuthenticated() &&
      !this._appUserSettingService.getIsLifeScience() &&
      !this._appUserSettingService.getIsQimaProduce() &&
      !this._window.parent.Cypress
    ) {
      this._freshChatService.initChat(userData);
    }
  }

  private _watch(): void {
    this._asideService.isAsideCollapsed$.pipe(untilDestroyed(this)).subscribe((isAsideCollapsed: boolean): void => {
      this.isAsideCollapsed = isAsideCollapsed;
      this._changeDetectorRef.markForCheck();
    });

    this._watchAppcues$().pipe(untilDestroyed(this)).subscribe();
    this._watchRouterForAppcues$().pipe(untilDestroyed(this)).subscribe();

    this._updateLoginInfo();
    this._updateUserData();
  }

  private _updateLoginInfo(): void {
    const channel = new BroadcastChannel(EChannelKeys.LOGIN_AGAIN);

    channel.onmessage = (event): void => {
      const { params, user } = event.data;
      const userId = this._storageService.getItem(EStorageKeys.USER_ID);

      if (!params || !user || userId === params.userId) {
        return;
      }

      this._userService.updateUserDataSession(user as IUser);
      void this._router.navigate(['/home']).then((isNavigated: boolean): void => {
        if (isNavigated) {
          this._window.location.reload();
        }
      });
    };
  }

  private _updateUserData(): void {
    const channel = new BroadcastChannel(EChannelKeys.UPDATE_USER_DATA);

    channel.onmessage = (event): void => {
      const { user } = event.data;

      if (user) {
        this._userService.updateUserDataSession(user as IUser);
        this._window.location.reload();
      }
    };
  }

  private _watchAppcues$(): Observable<boolean> {
    return this._appcuesService.appcuesEvent$().pipe(
      tap({
        next: (isVisible): void => {
          this.isAppcuesBannerVisible = isVisible;
          this._changeDetectorRef.markForCheck();
        },
      })
    );
  }

  private _watchRouterForAppcues$(): Observable<Event> {
    return this._router.events.pipe(
      filter((event: Event): boolean => {
        return event instanceof NavigationEnd;
      }),
      tap({
        next: (): void => {
          this._appcuesService.toggleBanner(false);
        },
      })
    );
  }
}
